<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { api, browserDeviceId, readableError } from "@/api";
import {
  PRIVACY_DOCUMENT_VERSION,
  TERMS_DOCUMENT_VERSION,
} from "@/legal-documents";
import { useAuthStore } from "@/stores/auth";

type AuthMode = "login" | "register";

const props = defineProps<{
  open: boolean;
  initialMode?: AuthMode;
}>();
const emit = defineEmits<{
  close: [];
  authenticated: [];
}>();

const auth = useAuthStore();
const mode = ref<AuthMode>(props.initialMode ?? "login");
const account = ref("");
const loginPassword = ref("");
const phone = ref("");
const registerPassword = ref("");
const otp = ref("");
const nickname = ref("");
const agreement = ref(false);
const challengeId = ref("");
const busy = ref(false);
const error = ref("");
const codeCountdown = ref(0);
const dialog = ref<HTMLElement>();
const deviceId = browserDeviceId();
let countdownTimer: ReturnType<typeof setInterval> | undefined;
let previouslyFocused: HTMLElement | null = null;

const canLogin = computed(
  () => account.value.trim().length > 0 && loginPassword.value.length >= 10,
);
const canRegister = computed(
  () =>
    challengeId.value.length > 0 &&
    otp.value.trim().length >= 4 &&
    registerPassword.value.length >= 10 &&
    nickname.value.trim().length > 0 &&
    agreement.value,
);

watch(
  () => props.initialMode,
  (value) => {
    if (value) switchMode(value);
  },
);
watch(
  () => props.open,
  async (value) => {
    if (value && props.initialMode) switchMode(props.initialMode);
    if (value) {
      previouslyFocused = document.activeElement as HTMLElement | null;
      await nextTick();
      dialog.value
        ?.querySelector<HTMLElement>(
          "input:not([disabled]), button:not([disabled]), a[href]",
        )
        ?.focus();
    } else {
      previouslyFocused?.focus();
      previouslyFocused = null;
    }
  },
  { immediate: true },
);

function handleDialogKeydown(event: KeyboardEvent) {
  if (event.key === "Escape") {
    event.preventDefault();
    emit("close");
    return;
  }
  if (event.key !== "Tab" || !dialog.value) return;
  const focusable = Array.from(
    dialog.value.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((element) => !element.hasAttribute("hidden"));
  if (focusable.length === 0) return;
  const first = focusable.at(0);
  const last = focusable.at(-1);
  if (!first || !last) return;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function normalizePhone(value: string) {
  const compact = value.replace(/[\s-]/g, "");
  if (compact.startsWith("+")) return compact;
  if (/^1\d{10}$/.test(compact)) return `+86${compact}`;
  return compact;
}

function switchMode(value: AuthMode) {
  mode.value = value;
  error.value = "";
}

function startCountdown(seconds: number) {
  codeCountdown.value = Math.max(1, seconds);
  if (countdownTimer) clearInterval(countdownTimer);
  countdownTimer = setInterval(() => {
    codeCountdown.value -= 1;
    if (codeCountdown.value <= 0 && countdownTimer) {
      clearInterval(countdownTimer);
      countdownTimer = undefined;
    }
  }, 1000);
}

async function requestRegisterOtp() {
  if (codeCountdown.value > 0 || busy.value) return;
  if (!agreement.value) {
    error.value = "请先阅读并明确同意用户服务协议和隐私政策";
    return;
  }
  busy.value = true;
  error.value = "";
  try {
    const response = await api.identity.requestOtp({
      phoneE164: normalizePhone(phone.value),
      purpose: "register",
      deviceId,
    });
    challengeId.value = response.challengeId;
    if (response.debugOtp) otp.value = response.debugOtp;
    startCountdown(response.retryAfterSeconds || 60);
  } catch (cause) {
    error.value = readableError(cause);
  } finally {
    busy.value = false;
  }
}

async function submitLogin() {
  if (!canLogin.value || busy.value) return;
  busy.value = true;
  error.value = "";
  try {
    const response = await api.identity.passwordLogin({
      account: normalizePhone(account.value),
      password: loginPassword.value,
      deviceId,
      deviceName: navigator.userAgent.slice(0, 80),
      platform: "web",
    });
    if (!response.profile) throw new Error("登录响应缺少用户资料");
    auth.setSession(response.profile);
    emit("authenticated");
  } catch (cause) {
    error.value = readableError(cause);
  } finally {
    busy.value = false;
  }
}

async function submitRegister() {
  if (!canRegister.value || busy.value) return;
  busy.value = true;
  error.value = "";
  try {
    const response = await api.identity.register({
      challengeId: challengeId.value,
      otp: otp.value.trim(),
      password: registerPassword.value,
      nickname: nickname.value.trim(),
      deviceId,
      deviceName: navigator.userAgent.slice(0, 80),
      platform: "web",
      termsDocumentVersion: TERMS_DOCUMENT_VERSION,
      privacyDocumentVersion: PRIVACY_DOCUMENT_VERSION,
      consentGranted: agreement.value,
    });
    if (!response.profile) throw new Error("注册响应缺少用户资料");
    auth.setSession(response.profile);
    emit("authenticated");
  } catch (cause) {
    error.value = readableError(cause);
  } finally {
    busy.value = false;
  }
}

onBeforeUnmount(() => {
  if (countdownTimer) clearInterval(countdownTimer);
  previouslyFocused?.focus();
});
</script>

<template>
  <div
    v-if="open"
    class="reference-auth-backdrop"
    role="presentation"
    @click.self="emit('close')"
  >
    <section
      ref="dialog"
      :class="['reference-auth-dialog', `is-${mode}`]"
      role="dialog"
      aria-modal="true"
      :aria-label="mode === 'register' ? '注册账号' : '账号登录'"
      @keydown="handleDialogKeydown"
    >
      <button
        class="reference-auth-close"
        type="button"
        aria-label="关闭"
        @click="emit('close')"
      >
        ×
      </button>
      <img
        class="reference-auth-logo"
        src="/demo-assets/mobile-logo.png"
        alt="178"
      />

      <template v-if="mode === 'login'">
        <h2>账号登录</h2>
        <form class="reference-auth-form" @submit.prevent="submitLogin">
          <input
            v-model="account"
            autocomplete="username"
            placeholder="请输入手机号码/178号"
          />
          <input
            v-model="loginPassword"
            autocomplete="current-password"
            type="password"
            placeholder="请输入登录密码"
          />
          <button
            class="reference-forgot"
            type="button"
            @click="error = '请通过新用户注册验证手机号，或联系管理员重置密码'"
          >
            忘记密码？
          </button>
          <button
            class="reference-auth-submit"
            type="submit"
            :disabled="busy || !canLogin"
          >
            {{ busy ? "登录中…" : "登 录" }}
          </button>
        </form>
        <button
          class="reference-auth-switch"
          type="button"
          @click="switchMode('register')"
        >
          新用户注册
        </button>
      </template>

      <template v-else>
        <h2>注册账号</h2>
        <form class="reference-auth-form" @submit.prevent="submitRegister">
          <input
            v-model="phone"
            autocomplete="tel"
            inputmode="tel"
            placeholder="请输入手机号码"
          />
          <input
            v-model="registerPassword"
            autocomplete="new-password"
            type="password"
            placeholder="请输入登录密码（至少10位）"
          />
          <label class="reference-code-row">
            <input
              v-model="otp"
              autocomplete="one-time-code"
              inputmode="numeric"
              maxlength="6"
              placeholder="请输入短信验证码"
            />
            <button
              type="button"
              :disabled="
                busy || codeCountdown > 0 || !phone.trim() || !agreement
              "
              @click="requestRegisterOtp"
            >
              {{ codeCountdown > 0 ? `${codeCountdown}s` : "获取验证码" }}
            </button>
          </label>
          <input
            v-model="nickname"
            autocomplete="nickname"
            maxlength="40"
            placeholder="请输入昵称"
          />
          <div class="reference-agreement">
            <input
              id="registration-legal-consent"
              v-model="agreement"
              type="checkbox"
            />
            <label for="registration-legal-consent">我已阅读并同意</label>
            <RouterLink to="/terms" target="_blank" rel="noopener"
              >《用户服务协议》</RouterLink
            >
            <span>和</span>
            <RouterLink to="/privacy" target="_blank" rel="noopener"
              >《隐私政策》</RouterLink
            >
          </div>
          <button
            class="reference-auth-submit"
            type="submit"
            :disabled="busy || !canRegister"
          >
            {{ busy ? "注册中…" : "注 册" }}
          </button>
        </form>
        <button
          class="reference-auth-switch"
          type="button"
          @click="switchMode('login')"
        >
          已有账号？<span>马上登录</span>
        </button>
      </template>

      <p v-if="error" class="reference-auth-error" role="alert">{{ error }}</p>
    </section>
  </div>
</template>
